import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import RoyaltyReportPanel from '../components/admin/RoyaltyReportPanel';
import QuarterlyChampionsPanel from '../components/admin/QuarterlyChampionsPanel';
import ForceLogoutPanel from '../components/admin/ForceLogoutPanel';
import BookFileVerifier from '../components/admin/BookFileVerifier';
import ChatRecoveryPanel from '../components/admin/ChatRecoveryPanel';
import ModerationPanel from '../components/admin/ModerationPanel';
import FeaturedAuthorsPanel from '../components/admin/FeaturedAuthorsPanel';
import BookAnalyticsDetailModal from '../components/admin/BookAnalyticsDetailModal';
import FeaturedBookPanel from '../components/admin/FeaturedBookPanel';
import FeaturedTrailersPanel from '../components/admin/FeaturedTrailersPanel';
import BookRecommendationsPanel from '../components/admin/BookRecommendationsPanel';
import AIBotPanel from '../components/admin/AIBotPanel';
import AIDiscussionDailyCap from '../components/admin/AIDiscussionDailyCap';
import AIPersonaCap from '../components/admin/AIPersonaCap';
import ReliabilityPanel from '../components/admin/ReliabilityPanel';
import ReadingAnalyticsPanel from '../components/admin/ReadingAnalyticsPanel';
import { useAIBot } from '../hooks/useAIBot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper for cover URLs
const getAssetBaseUrl = () => window.location.origin;
const getCoverUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${getAssetBaseUrl()}${url}`;
  return url;
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Featured Book State
  const [books, setBooks] = useState([]);
  const [featuredBookId, setFeaturedBookId] = useState('');
  const [currentFeaturedBook, setCurrentFeaturedBook] = useState(null);
  const [savingFeatured, setSavingFeatured] = useState(false);
  
  // Featured Trailers State (3 slots: left, middle, right)
  const [booksWithTrailers, setBooksWithTrailers] = useState([]);
  const [featuredTrailers, setFeaturedTrailers] = useState({});
  const [savingTrailer, setSavingTrailer] = useState(false);
  // Embed Reliability panel
  const [reliabilityStats, setReliabilityStats] = useState(null);
  const [reliabilityDays, setReliabilityDays] = useState(30);
  const [loadingReliability, setLoadingReliability] = useState(false);
  
  // Recommended Books State
  const [recommendedBooks, setRecommendedBooks] = useState([]);
  const [savingRecommendations, setSavingRecommendations] = useState(false);
  
  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [selectedBookAnalytics, setSelectedBookAnalytics] = useState(null);
  const [showBookDetails, setShowBookDetails] = useState(false);
  const [analyticsLastFetched, setAnalyticsLastFetched] = useState(null);
  const [analyticsTick, setAnalyticsTick] = useState(0);

  const userRole = localStorage.getItem('userRole');

  // AI bots — encapsulates fetch/toggle/generate/poll in one custom hook.
  const reviewer = useAIBot({
    endpointBase: 'ai-reviewer',
    pollField: 'reviews_generated',
    successLabel: 'AI reviews',
    kindLabel: 'AI Reviews',
    enabled: userRole === 'admin',
  });
  const discussion = useAIBot({
    endpointBase: 'ai-discussion',
    pollField: 'messages_generated',
    successLabel: 'AI chat messages',
    kindLabel: 'AI Discussion',
    enabled: userRole === 'admin',
  });

  useEffect(() => {
    if (userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchBooks();
    fetchFeaturedBookSetting();
    fetchBooksWithTrailers();
    fetchFeaturedTrailerSetting();
    fetchRecommendations();
    fetchAnalytics();
  }, [userRole, navigate]);

  // Auto-refresh per-book analytics every 30s + tick the "Refreshed Xs ago"
  // label every second so it ages naturally between fetches.
  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    const tickIv = setInterval(() => setAnalyticsTick((t) => t + 1), 1000);
    const fetchIv = setInterval(() => fetchAnalytics(true), 30_000);
    return () => {
      clearInterval(tickIv);
      clearInterval(fetchIv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // Live "new reader joined" feed — every 12s pull recent reading activity
  // newer than our cursor and surface each fresh event as a discrete toast.
  // Dedupes on (user_id, book_id, status_bucket) so we don't re-toast the
  // same reader over and over when their progress keeps getting saved
  // (which bumps `updated_at` and would otherwise re-fire the poll filter).
  const lastActivityCursor = React.useRef(new Date().toISOString());
  const toastedKeysRef = React.useRef(new Set());
  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    const pull = async () => {
      try {
        const token = localStorage.getItem('token');
        const url = `${API}/admin/analytics/recent-activity?since=${encodeURIComponent(lastActivityCursor.current)}&limit=10`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const events = res.data?.events || [];
        if (events.length) {
          // Reverse so the OLDEST new event toasts first
          events.slice().reverse().forEach((ev) => {
            // Bucket the status so micro-updates (e.g. progress goes from
            // 30% → 35%) don't fire a new toast — we only fire when a reader
            // crosses into a new state (just-opened / actively-reading / finished).
            const bucket =
              ev.status === 'completed' ? 'finished'
              : ev.progress_percentage >= 25 ? 'reading'
              : 'opened';
            const key = `${ev.user_id || 'anon'}|${ev.book_id || ''}|${bucket}`;
            if (toastedKeysRef.current.has(key)) return;
            toastedKeysRef.current.add(key);
            // Cap the dedupe set so it can't grow forever in long admin sessions
            if (toastedKeysRef.current.size > 500) {
              const it = toastedKeysRef.current.values();
              for (let i = 0; i < 100; i += 1) toastedKeysRef.current.delete(it.next().value);
            }
            const verb = bucket === 'finished' ? 'just finished'
              : bucket === 'reading' ? 'is reading'
              : 'just opened';
            toast({
              title: `📖 ${ev.user_name} ${verb}`,
              description: ev.book_title,
              duration: 6000,
            });
          });
          lastActivityCursor.current = events[0].at || res.data?.fetched_at || lastActivityCursor.current;
        } else if (res.data?.fetched_at) {
          lastActivityCursor.current = res.data.fetched_at;
        }
      } catch (error) {
        // Silent toast-wise — don't spam the admin with errors about a broken
        // poll — but log to console so devtools can show real failures.
        console.debug('[AdminSettings] live-activity poll failed:', error?.message || error);
      }
    };
    const iv = setInterval(pull, 12_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const fetchBooksWithTrailers = async () => {
    try {
      const response = await axios.get(`${API}/books-with-trailers`);
      setBooksWithTrailers(response.data || []);
    } catch (error) {
      console.log('No books with trailers found');
    }
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendedBooks(response.data.book_ids || []);
    } catch (error) {
      console.log('No recommendations set yet');
    }
  };

  const handleSaveRecommendations = async () => {
    setSavingRecommendations(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/recommendations`, recommendedBooks, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast({
        title: 'Recommendations Updated',
        description: `${recommendedBooks.length} book(s) set as recommended.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save recommendations',
        variant: 'destructive'
      });
    } finally {
      setSavingRecommendations(false);
    }
  };

  const toggleRecommendedBook = (bookId) => {
    setRecommendedBooks(prev => {
      if (prev.includes(bookId)) {
        return prev.filter(id => id !== bookId);
      } else if (prev.length < 10) {
        return [...prev, bookId];
      } else {
        toast({
          title: 'Maximum Reached',
          description: 'You can only recommend up to 10 books',
          variant: 'destructive'
        });
        return prev;
      }
    });
  };

  const fetchFeaturedTrailerSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings/featured-trailers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.trailers) {
        const trailerMap = {};
        for (const t of response.data.trailers) {
          if (t.slot && t.book_id) {
            const book = booksWithTrailers.find(b => b.id === t.book_id);
            trailerMap[t.slot] = book || { id: t.book_id };
          }
        }
        setFeaturedTrailers(trailerMap);
      }
    } catch (error) {
      console.log('No featured trailers set yet');
    }
  };

  const handleSetTrailer = async (position, bookId) => {
    if (!bookId) {
      // Remove trailer from this position
      handleRemoveTrailer(position);
      return;
    }

    setSavingTrailer(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/featured-trailer?book_id=${bookId}&position=${position}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const book = booksWithTrailers.find(b => b.id === bookId);
      setFeaturedTrailers(prev => ({ ...prev, [position]: book }));
      
      toast({
        title: 'Trailer Updated',
        description: `"${book?.title}" set to Slot ${position}.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to set trailer',
        variant: 'destructive'
      });
    } finally {
      setSavingTrailer(false);
    }
  };

  const handleRemoveTrailer = async (position) => {
    setSavingTrailer(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/settings/featured-trailer/${position}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFeaturedTrailers(prev => ({ ...prev, [position]: null }));
      
      toast({
        title: 'Trailer Removed',
        description: `Removed trailer from Slot ${position}.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove trailer',
        variant: 'destructive'
      });
    } finally {
      setSavingTrailer(false);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchFeaturedBookSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings/featured-book`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.book_id) {
        setFeaturedBookId(response.data.book_id);
        // Find the book details
        const bookResponse = await axios.get(`${API}/books/${response.data.book_id}`);
        setCurrentFeaturedBook(bookResponse.data);
      }
    } catch (error) {
      console.log('No featured book set yet');
    }
  };

  const fetchAnalytics = async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setAnalyticsLastFetched(Date.now());
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (!silent) {
        toast({
          title: 'Error',
          description: 'Failed to load analytics',
          variant: 'destructive'
        });
      }
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // ===== Embed Reliability Panel =====
  const fetchReliabilityStats = async (days) => {
    setLoadingReliability(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/analytics/embed-fallback-stats?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReliabilityStats(response.data);
    } catch {
      setReliabilityStats({ days, total_events: 0, rows: [] });
    } finally {
      setLoadingReliability(false);
    }
  };


  const fetchBookAnalytics = async (bookId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/analytics/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBookAnalytics(response.data);
      setShowBookDetails(true);
    } catch (error) {
      console.error('Error fetching book analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load book details',
        variant: 'destructive'
      });
    }
  };

  const handleSaveFeaturedBook = async () => {
    if (!featuredBookId) {
      toast({
        title: 'Error',
        description: 'Please select a book',
        variant: 'destructive'
      });
      return;
    }

    setSavingFeatured(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/admin/settings/featured-book`, 
        { book_id: featuredBookId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      // Update current featured book display
      const selectedBook = books.find(b => b.id === featuredBookId);
      setCurrentFeaturedBook(selectedBook);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save featured book',
        variant: 'destructive'
      });
    } finally {
      setSavingFeatured(false);
    }
  };

  if (userRole !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Settings className="h-8 w-8 mr-3 text-red-500" />
              Admin Settings
            </h1>
            <p className="text-gray-400 mt-1">Configure platform settings and view analytics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Featured Book Section */}
          <FeaturedBookPanel
            currentFeaturedBook={currentFeaturedBook}
            books={books}
            featuredBookId={featuredBookId}
            onFeaturedBookIdChange={setFeaturedBookId}
            onSave={handleSaveFeaturedBook}
            saving={savingFeatured}
          />

          {/* Featured Trailers Section - 5 Slots */}
          <FeaturedTrailersPanel
            booksWithTrailers={booksWithTrailers}
            featuredTrailers={featuredTrailers}
            savingTrailer={savingTrailer}
            onSetTrailer={handleSetTrailer}
            onRemoveTrailer={handleRemoveTrailer}
            getCoverUrl={getCoverUrl}
          />

          {/* Book Recommendations Section */}
          <BookRecommendationsPanel
            books={books}
            recommendedBooks={recommendedBooks}
            savingRecommendations={savingRecommendations}
            onToggleBook={toggleRecommendedBook}
            onSave={handleSaveRecommendations}
            getCoverUrl={getCoverUrl}
          />
        </div>

        {/* Row 2: AI Reviewer + AI Discussion */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <AIBotPanel
            title="AI Book Reviewer"
            color="purple"
            testIdPrefix="ai-reviewer"
            status={{
              enabled: reviewer.status?.enabled,
              total: reviewer.status?.total_ai_reviews,
              last_run: reviewer.status?.last_run,
              recent_items: reviewer.status?.recent_reviews,
            }}
            totalLabel="Total AI Reviews"
            description="Generates realistic book reviews with random human names. Auto-runs every ~48 hours."
            actionLabel="Generate Reviews Now"
            actionHint="Generates 3-4 reviews across random books"
            loadingLabel="Generating Reviews..."
            offMessage="Toggle the switch above to enable automatic reviews"
            toggling={reviewer.toggling}
            running={reviewer.generating}
            runTestId="generate-ai-reviews-btn"
            renderItem={(r) => (
              <div
                key={r.id || `${r.user_id || r.user_name}-${r.timestamp || r.created_at || ''}`}
                className="flex items-center justify-between bg-white/5 rounded px-3 py-2 text-sm"
              >
                <span className="text-white truncate mr-2">{r.user_name}</span>
                <span className="text-yellow-400 shrink-0">{'★'.repeat(r.rating)}</span>
              </div>
            )}
            onToggle={reviewer.toggle}
            onRun={reviewer.generate}
          />

          <AIBotPanel
            title="AI Book Discussion"
            color="cyan"
            testIdPrefix="ai-discussion"
            status={{
              enabled: discussion.status?.enabled,
              total: discussion.status?.total_ai_messages,
              last_run: discussion.status?.last_run,
              recent_items: discussion.status?.recent_messages,
            }}
            totalLabel="Total AI Messages"
            description="Posts realistic chat messages in 3-4 random book rooms each week. Messages reference the book's themes/characters and use varied human-sounding voices."
            actionLabel="Post Discussion Now"
            actionHint="Posts 2-3 messages per book across the catalogue (respects daily cap)"
            loadingLabel="Generating Discussion..."
            offMessage="Toggle the switch above to seed organic chat in book rooms"
            toggling={discussion.toggling}
            running={discussion.generating}
            runTestId="generate-ai-discussion-btn"
            renderItem={(m, i) => (
              <div key={m.id || `${m.user_name}-${m.timestamp || i}`} className="bg-white/5 rounded px-3 py-2 text-sm">
                <span className="text-cyan-300 font-medium">{m.user_name}: </span>
                <span className="text-gray-300">{(m.content || '').slice(0, 90)}{m.content?.length > 90 ? '…' : ''}</span>
              </div>
            )}
            onToggle={discussion.toggle}
            onRun={discussion.generate}
            extraControls={
              <>
                <AIDiscussionDailyCap
                  initialCap={discussion.status?.daily_cap_per_book}
                  defaultCap={discussion.status?.default_daily_cap_per_book ?? 5}
                  onSaved={() => discussion.refresh && discussion.refresh()}
                />
                <AIPersonaCap
                  initialCap={discussion.status?.quarterly_persona_cap}
                  defaultCap={discussion.status?.default_quarterly_persona_cap ?? 15}
                  onSaved={() => discussion.refresh && discussion.refresh()}
                />
              </>
            }
          />
        </div>

        {/* Reliability Panel */}
        <ReliabilityPanel
          stats={reliabilityStats}
          days={reliabilityDays}
          loading={loadingReliability}
          onDaysChange={(v) => {
            setReliabilityDays(v);
            fetchReliabilityStats(v);
          }}
          onRefresh={() => fetchReliabilityStats(reliabilityDays)}
        />

        {/* Book File Integrity Verifier */}
        <BookFileVerifier />

        {/* Chat Moderation — flagged comments review + mute management */}
        <ModerationPanel />

        {/* Chat Recovery — restore orphaned book discussions after re-uploads */}
        <ChatRecoveryPanel books={books} />

        {/* Featured Authors — power the "Authors we're featuring" carousel on /about */}
        <FeaturedAuthorsPanel />

        {/* Royalty Report Panel */}
        <RoyaltyReportPanel />

        <QuarterlyChampionsPanel />

        {/* Security — Force Logout All Users */}
        <ForceLogoutPanel />

        {/* Reading Analytics Summary + Per-Book Table */}
        <ReadingAnalyticsPanel
          analytics={analytics}
          loading={loadingAnalytics}
          analyticsLastFetched={analyticsLastFetched}
          analyticsTick={analyticsTick}
          onRefresh={() => fetchAnalytics(true)}
          onOpenBookDetail={fetchBookAnalytics}
        />

        {/* Book Analytics Detail Modal */}
        {showBookDetails && selectedBookAnalytics && (
          <BookAnalyticsDetailModal
            analytics={selectedBookAnalytics}
            onClose={() => setShowBookDetails(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
